import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { toast } from "../services/toast"

/**
 * Window Manager Controller
 * 
 * Orchestrates all desktop windows: creation, focus, arrangement, and real-time updates.
 * 
 * Targets:
 * - container: Area where windows are rendered
 * - taskbar: Bar showing minimized/open windows
 * - menuItem: Menu items that open windows
 * 
 * Actions:
 * - openWindow: Opens a new window from menu
 * - arrangeWindows: Arranges windows (tile, cascade, minimize-all)
 * - focusWindow: Brings a window to front (from taskbar)
 * - restoreWindow: Restores a minimized window
 */
export default class extends Controller {
  static targets = ["container", "taskbar"]
  static values = {
    maxWindows: { type: Number, default: 8 },
    defaultWindow: { type: Object, default: {} }
  }

  // Window registry: Map<windowId, { element, title, minimized, zIndex }>
  windows = new Map()
  
  // Z-index management
  baseZIndex = 100
  currentZIndex = 100

  // ActionCable consumer
  consumer = null
  catalogSubscription = null
  
  // LocalStorage key for persisting window state
  // (stored in sessionStorage for per-tab isolation)
  storageKey = "desktop_windows_state"

  connect() {
    this.bindEventHandlers()
    this.setupCatalogChannel()
    this.setupWindowEventListeners()
    this.setupTopFrameInterceptor()
    this.setupPopStateGuard()
    this.setupBeforeUnload()
    
    // Restore saved windows or open default
    this.restoreWindowsFromStorage()
  }

  disconnect() {
    this.teardownPopStateGuard()
    this.teardownBeforeUnload()
    this.teardownTopFrameInterceptor()
    this.teardownWindowEventListeners()
    if (this.catalogSubscription) {
      this.catalogSubscription.unsubscribe()
    }
    if (this.consumer) {
      this.consumer.disconnect()
      this.consumer = null
    }
  }

  bindEventHandlers() {
    this.boundHandleWindowFocus = this.handleWindowFocus.bind(this)
    this.boundHandleWindowClose = this.handleWindowClose.bind(this)
    this.boundHandleWindowMinimize = this.handleWindowMinimize.bind(this)
    this.boundHandleWindowRestore = this.handleWindowRestore.bind(this)
    this.boundHandleTitleChange = this.handleTitleChange.bind(this)
    this.boundHandleWindowRegistered = this.handleWindowRegistered.bind(this)
    this.boundHandleWindowStateChange = this.handleWindowStateChange.bind(this)
    this.boundHandleWindowSrcChange = this.handleWindowSrcChange.bind(this)
    this.boundInterceptTopFrame = this.interceptTopFrame.bind(this)
    this.boundHandlePopState = this.handlePopState.bind(this)
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this)
  }

  // ==========================================
  // Browser Navigation Guards
  // ==========================================

  /**
   * Pushes an extra history entry and intercepts popstate to prevent
   * browser back/forward (two-finger swipe) from leaving the desktop.
   */
  setupPopStateGuard() {
    window.history.pushState({ desktop: true }, "", window.location.href)
    window.addEventListener("popstate", this.boundHandlePopState)
  }

  teardownPopStateGuard() {
    window.removeEventListener("popstate", this.boundHandlePopState)
  }

  handlePopState(event) {
    // Re-push to keep the user on the desktop page
    window.history.pushState({ desktop: true }, "", window.location.href)
  }

  /**
   * Warns the user before leaving when forms have unsaved changes.
   */
  setupBeforeUnload() {
    window.addEventListener("beforeunload", this.boundHandleBeforeUnload)
  }

  teardownBeforeUnload() {
    window.removeEventListener("beforeunload", this.boundHandleBeforeUnload)
  }

  handleBeforeUnload(event) {
    const dirtyForms = this.containerTarget.querySelectorAll("form[data-dirty='true']")
    if (dirtyForms.length > 0) {
      event.preventDefault()
      event.returnValue = ""
    }
  }

  // ==========================================
  // Top Frame Interceptor
  // ==========================================

  /**
   * Intercepts clicks on links with data-turbo-frame="_top" inside desktop windows
   * and redirects them to the window's content turbo-frame instead of doing a full
   * page navigation that would destroy the desktop layout.
   */
  setupTopFrameInterceptor() {
    this.element.addEventListener("click", this.boundInterceptTopFrame, true)
  }

  teardownTopFrameInterceptor() {
    this.element.removeEventListener("click", this.boundInterceptTopFrame, true)
  }

  interceptTopFrame(event) {
    const link = event.target.closest('a[data-turbo-frame="_top"]')
    if (!link) return

    const contentFrame = link.closest('turbo-frame[id$="-content"]')
    if (!contentFrame) return

    event.preventDefault()
    contentFrame.src = link.href
  }

  // ==========================================
  // Window Creation
  // ==========================================

  /**
   * Restores windows from localStorage or opens default window
   */
  restoreWindowsFromStorage() {
    const savedState = this.loadWindowsState()
    
    if (savedState && savedState.windows && savedState.windows.length > 0) {
      // Restore saved windows
      savedState.windows.forEach((windowState, index) => {
        setTimeout(() => {
          this.createWindowFromState(windowState)
        }, index * 50) // Slight delay between windows for smoother loading
      })
    } else {
      // No saved state, open default window
      this.openDefaultWindow()
    }
  }

  /**
   * Opens the default window (Dashboard) on page load
   */
  openDefaultWindow() {
    if (this.hasDefaultWindowValue && this.defaultWindowValue.src) {
      const { title, src } = this.defaultWindowValue
      const windowId = `window-${this.generateId()}`
      this.createWindow({ windowId, title: title || "Dashboard", src, maximized: true })
    }
  }

  /**
   * Opens a new window from a menu item click
   * @param {Event} event - Click event with params
   */
  openWindow(event) {
    event.preventDefault()

    const { id, title, src, size } = event.params

    // Check if window with same src already exists
    const existingWindow = this.findWindowBySrc(src)
    if (existingWindow) {
      this.bringToFront(existingWindow.id)
      const windowEl = this.windows.get(existingWindow.id)?.element
      if (windowEl) {
        const controller = this.application.getControllerForElementAndIdentifier(windowEl, "window")
        if (controller?.isMinimized) {
          controller.restore()
        }
      }
      return
    }

    // Check max windows limit
    if (this.windows.size >= this.maxWindowsValue) {
      this.showMaxWindowsWarning()
      return
    }

    const windowId = `window-${this.generateId()}`
    // All windows from menu open maximized by default
    this.createWindow({ windowId, title, src, size, maximized: true })
  }

  /**
   * Creates a window from saved state (for restoration)
   */
  createWindowFromState(state) {
    const { title, src, originalSrc, x, y, width, height, minimized, maximized, zIndex, navHistory, navIndex } = state
    
    // Check if window with same originalSrc already exists
    const existingWindow = this.findWindowBySrc(originalSrc || src)
    if (existingWindow) return
    
    const windowId = `window-${this.generateId()}`
    this.createWindow({ 
      windowId, 
      title, 
      src, // Use current src (which may be different from original after navigation)
      originalSrc: originalSrc || src,
      size: "medium",
      maximized,
      restoreState: { x, y, width, height, minimized, zIndex, navHistory, navIndex }
    })
  }

  /**
   * Creates a new window element and adds it to the container
   */
  createWindow({ windowId, title, src, originalSrc = null, size = "medium", maximized = true, restoreState = null }) {
    // Calculate position offset based on existing windows
    const offset = (this.windows.size % 5) * 30
    const x = restoreState?.x ?? (50 + offset)
    const y = restoreState?.y ?? (50 + offset)
    const customWidth = restoreState?.width
    const customHeight = restoreState?.height

    // Create window HTML
    const windowHtml = this.buildWindowHtml({ windowId, title, src, size, x, y, maximized, customWidth, customHeight })
    
    // Insert into container
    this.containerTarget.insertAdjacentHTML("beforeend", windowHtml)

    const windowElement = document.getElementById(windowId)
    
    // Register window
    this.registerWindow(windowId, {
      element: windowElement,
      title,
      src,
      originalSrc: originalSrc || src, // Keep track of original src for menu matching
      minimized: false
    })

    // Bring to front
    this.bringToFront(windowId)

    // Update taskbar
    this.updateTaskbar()
    
    // Save state after creating window
    this.saveWindowsState()
    
    // Handle maximized/minimized state
    if (maximized || restoreState?.maximized) {
      // Wait for Stimulus to connect
      requestAnimationFrame(() => {
        const controller = this.application.getControllerForElementAndIdentifier(windowElement, "window")
        if (controller) {
          // Restore navigation history if available
          if (restoreState?.navHistory?.length > 0) {
            controller.navHistory = restoreState.navHistory
            controller.navIndex = restoreState.navIndex ?? restoreState.navHistory.length - 1
            controller.updateNavButtons()
          }

          controller.maximize()
          
          // If restoring minimized state, minimize after maximizing
          if (restoreState?.minimized) {
            setTimeout(() => controller.minimize(), 100)
          }
        }
      })
    } else if (restoreState?.minimized) {
      requestAnimationFrame(() => {
        const controller = this.application.getControllerForElementAndIdentifier(windowElement, "window")
        if (controller) {
          if (restoreState?.navHistory?.length > 0) {
            controller.navHistory = restoreState.navHistory
            controller.navIndex = restoreState.navIndex ?? restoreState.navHistory.length - 1
            controller.updateNavButtons()
          }
          controller.minimize()
        }
      })
    } else if (restoreState?.navHistory?.length > 0) {
      requestAnimationFrame(() => {
        const controller = this.application.getControllerForElementAndIdentifier(windowElement, "window")
        if (controller) {
          controller.navHistory = restoreState.navHistory
          controller.navIndex = restoreState.navIndex ?? restoreState.navHistory.length - 1
          controller.updateNavButtons()
        }
      })
    }
  }

  buildWindowHtml({ windowId, title, src, size, x, y, customWidth, customHeight }) {
    const sizes = {
      small: { width: 400, height: 300 },
      medium: { width: 600, height: 450 },
      large: { width: 800, height: 600 },
      xlarge: { width: 1000, height: 700 }
    }
    
    const { width: defaultWidth, height: defaultHeight } = sizes[size] || sizes.medium
    const width = customWidth || defaultWidth
    const height = customHeight || defaultHeight
    const frameId = `${windowId}-content`

    return `
      <div id="${windowId}"
           class="desktop-window is-resizable"
           style="width: ${width}px; height: ${height}px; left: ${x}px; top: ${y}px;"
           data-window-manager-target="window"
           data-controller="window"
           data-window-window-id-value="${windowId}"
           data-window-resizable-value="true"
           data-action="mousedown->window#focus touchstart->window#focus">

        <div class="window-header" data-window-target="header">
          <div class="window-nav">
            <button type="button" class="window-nav-btn" disabled
                    data-window-target="navBack"
                    data-action="click->window#goBack" aria-label="Atrás">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button type="button" class="window-nav-btn" disabled
                    data-window-target="navForward"
                    data-action="click->window#goForward" aria-label="Adelante">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
          <div class="window-title">
            <svg class="window-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            </svg>
            <span data-window-target="title">${this.escapeHtml(title)}</span>
          </div>

          <div class="window-controls">
            <button type="button" class="window-control window-control--minimize"
                    data-action="click->window#minimize" aria-label="Minimizar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
            <button type="button" class="window-control window-control--maximize"
                    data-action="click->window#toggleMaximize" aria-label="Maximizar">
              <svg data-window-target="maximizeIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              </svg>
            </button>
            <button type="button" class="window-control window-control--close"
                    data-action="click->window#close" aria-label="Cerrar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <div class="window-content" data-window-target="content" data-turbo-prefetch="false">
          <div class="window-loading is-hidden" data-window-target="loading">
            <div class="window-spinner"></div>
            <span>Cargando...</span>
          </div>

          <turbo-frame id="${frameId}" src="${src}" data-turbo-action="replace">
          </turbo-frame>
        </div>

        <div class="resize-handle resize-handle--n" data-window-target="resizer" data-direction="n"></div>
        <div class="resize-handle resize-handle--e" data-window-target="resizer" data-direction="e"></div>
        <div class="resize-handle resize-handle--s" data-window-target="resizer" data-direction="s"></div>
        <div class="resize-handle resize-handle--w" data-window-target="resizer" data-direction="w"></div>
        <div class="resize-handle resize-handle--ne" data-window-target="resizer" data-direction="ne"></div>
        <div class="resize-handle resize-handle--se" data-window-target="resizer" data-direction="se"></div>
        <div class="resize-handle resize-handle--sw" data-window-target="resizer" data-direction="sw"></div>
        <div class="resize-handle resize-handle--nw" data-window-target="resizer" data-direction="nw"></div>
      </div>
    `
  }

  // ==========================================
  // Window Registry
  // ==========================================

  registerWindow(windowId, data) {
    this.windows.set(windowId, {
      ...data,
      zIndex: ++this.currentZIndex
    })
  }

  unregisterWindow(windowId) {
    const windowData = this.windows.get(windowId)
    
    // Remove from registry
    this.windows.delete(windowId)
    
    // Also remove element from DOM if still present
    if (windowData?.element && windowData.element.parentNode) {
      windowData.element.remove()
    }
  }

  findWindowBySrc(src) {
    for (const [id, data] of this.windows) {
      // Check if src matches original src (for menu matching) AND element is still in DOM
      if (data.originalSrc === src && data.element && document.contains(data.element)) {
        return { id, ...data }
      }
    }
    return null
  }

  // ==========================================
  // Focus Management
  // ==========================================

  bringToFront(windowId) {
    const windowData = this.windows.get(windowId)
    if (!windowData) return

    this.currentZIndex++
    windowData.zIndex = this.currentZIndex
    windowData.element.style.zIndex = this.currentZIndex

    // Update focused state
    this.windows.forEach((data, id) => {
      data.element.classList.toggle("is-focused", id === windowId)
    })

    this.updateTaskbar()
  }

  handleWindowFocus(event) {
    const { windowId } = event.detail
    this.bringToFront(windowId)
  }

  // ==========================================
  // Window Events
  // ==========================================

  setupWindowEventListeners() {
    // Listen for window events from child controllers
    this.element.addEventListener("window:focus", this.boundHandleWindowFocus)
    this.element.addEventListener("window:close", this.boundHandleWindowClose)
    this.element.addEventListener("window:minimize", this.boundHandleWindowMinimize)
    this.element.addEventListener("window:restore", this.boundHandleWindowRestore)
    this.element.addEventListener("window:titleChange", this.boundHandleTitleChange)
    this.element.addEventListener("window:registered", this.boundHandleWindowRegistered)
    this.element.addEventListener("window:stateChange", this.boundHandleWindowStateChange)
    this.element.addEventListener("window:srcChange", this.boundHandleWindowSrcChange)
  }

  teardownWindowEventListeners() {
    this.element.removeEventListener("window:focus", this.boundHandleWindowFocus)
    this.element.removeEventListener("window:close", this.boundHandleWindowClose)
    this.element.removeEventListener("window:minimize", this.boundHandleWindowMinimize)
    this.element.removeEventListener("window:restore", this.boundHandleWindowRestore)
    this.element.removeEventListener("window:titleChange", this.boundHandleTitleChange)
    this.element.removeEventListener("window:registered", this.boundHandleWindowRegistered)
    this.element.removeEventListener("window:stateChange", this.boundHandleWindowStateChange)
    this.element.removeEventListener("window:srcChange", this.boundHandleWindowSrcChange)
  }

  handleWindowRegistered(event) {
    // Window was registered via ViewComponent (not createWindow), add to our map if not exists
    const { windowId } = event.detail
    
    // Skip if already registered (createWindow already registered it)
    if (this.windows.has(windowId)) {
      return
    }
    
    const element = document.getElementById(windowId)
    if (element) {
      const titleEl = element.querySelector("[data-window-target='title']")
      const frameEl = element.querySelector("turbo-frame[src]")
      this.registerWindow(windowId, {
        element,
        title: titleEl?.textContent || "Ventana",
        src: frameEl?.getAttribute("src") || "",
        minimized: false
      })
      this.updateTaskbar()
    }
  }

  handleWindowClose(event) {
    const { windowId } = event.detail

    // Immediately unregister to allow reopening
    this.windows.delete(windowId)

    // Force taskbar update
    this.updateTaskbar()
    
    // Save state after closing window
    this.saveWindowsState()
  }

  handleWindowMinimize(event) {
    const { windowId, title } = event.detail
    const windowData = this.windows.get(windowId)
    if (windowData) {
      windowData.minimized = true
      if (title) windowData.title = title
      this.updateTaskbar()
      this.saveWindowsState()
    }
  }

  handleWindowRestore(event) {
    const { windowId } = event.detail
    const windowData = this.windows.get(windowId)
    if (windowData) {
      windowData.minimized = false
      this.bringToFront(windowId)
      this.updateTaskbar()
      this.saveWindowsState()
    }
  }

  handleTitleChange(event) {
    const { windowId, title } = event.detail
    const windowData = this.windows.get(windowId)
    if (windowData) {
      windowData.title = title
      this.updateTaskbar()
      this.saveWindowsState()
    }
  }

  handleWindowStateChange(event) {
    // Window position/size changed, save state
    this.saveWindowsState()
  }

  handleWindowSrcChange(event) {
    // Window navigated to a new URL, update src and save state
    const { windowId, src } = event.detail
    const windowData = this.windows.get(windowId)
    if (windowData) {
      windowData.src = src
      this.saveWindowsState()
    }
  }

  // ==========================================
  // Taskbar
  // ==========================================

  updateTaskbar() {
    if (!this.hasTaskbarTarget) return

    const items = []
    
    this.windows.forEach((data, windowId) => {
      const classes = ["taskbar-item"]
      if (data.element.classList.contains("is-focused")) classes.push("is-active")
      if (data.minimized) classes.push("is-minimized")

      items.push(`
        <button type="button" 
                class="${classes.join(" ")}"
                data-action="click->window-manager#taskbarClick"
                data-window-id="${windowId}">
          ${this.escapeHtml(data.title)}
        </button>
      `)
    })

    this.taskbarTarget.innerHTML = items.join("")
  }

  taskbarClick(event) {
    const windowId = event.currentTarget.dataset.windowId
    const windowData = this.windows.get(windowId)
    
    if (!windowData) return

    if (windowData.minimized) {
      // Restore minimized window
      const controller = this.application.getControllerForElementAndIdentifier(
        windowData.element, 
        "window"
      )
      controller?.restore()
    } else if (windowData.element.classList.contains("is-focused")) {
      // Minimize if already focused
      const controller = this.application.getControllerForElementAndIdentifier(
        windowData.element, 
        "window"
      )
      controller?.minimize()
    } else {
      // Bring to front
      this.bringToFront(windowId)
    }
  }

  // ==========================================
  // Window Arrangement
  // ==========================================

  arrangeWindows(event) {
    const type = event.params?.type || "tile"

    switch (type) {
      case "tile":
        this.tileWindows()
        break
      case "cascade":
        this.cascadeWindows()
        break
      case "minimize-all":
        this.minimizeAll()
        break
      case "close-all":
        this.closeAll()
        break
    }
  }

  tileWindows() {
    const visibleWindows = this.getVisibleWindows()
    if (visibleWindows.length === 0) return

    const container = this.containerTarget
    const containerRect = container.getBoundingClientRect()
    const padding = 10
    const taskbarHeight = this.hasTaskbarTarget ? 48 : 0

    const availableWidth = containerRect.width - padding * 2
    const availableHeight = containerRect.height - padding * 2 - taskbarHeight

    const count = visibleWindows.length
    const cols = Math.ceil(Math.sqrt(count))
    const rows = Math.ceil(count / cols)

    const windowWidth = Math.floor(availableWidth / cols) - padding
    const windowHeight = Math.floor(availableHeight / rows) - padding

    visibleWindows.forEach((windowId, index) => {
      const col = index % cols
      const row = Math.floor(index / cols)

      const x = padding + col * (windowWidth + padding)
      const y = padding + row * (windowHeight + padding)

      const windowData = this.windows.get(windowId)
      if (windowData) {
        const el = windowData.element
        el.style.left = `${x}px`
        el.style.top = `${y}px`
        el.style.width = `${windowWidth}px`
        el.style.height = `${windowHeight}px`
        el.classList.remove("is-maximized")
        
        // Clear preMaximizeState since window is no longer maximized
        const controller = this.application.getControllerForElementAndIdentifier(el, "window")
        if (controller) {
          controller.isMaximized = false
          controller.preMaximizeState = null
        }
      }
    })
    
    // Save state after arranging
    this.saveWindowsState()
  }

  cascadeWindows() {
    const visibleWindows = this.getVisibleWindows()
    if (visibleWindows.length === 0) return

    const offsetX = 30
    const offsetY = 30
    const baseWidth = 600
    const baseHeight = 450

    visibleWindows.forEach((windowId, index) => {
      const windowData = this.windows.get(windowId)
      if (windowData) {
        const el = windowData.element
        el.style.left = `${50 + index * offsetX}px`
        el.style.top = `${50 + index * offsetY}px`
        el.style.width = `${baseWidth}px`
        el.style.height = `${baseHeight}px`
        el.classList.remove("is-maximized")
        
        // Clear preMaximizeState since window is no longer maximized
        const controller = this.application.getControllerForElementAndIdentifier(el, "window")
        if (controller) {
          controller.isMaximized = false
          controller.preMaximizeState = null
        }
        
        // Update z-index
        this.bringToFront(windowId)
      }
    })
    
    // Save state after arranging
    this.saveWindowsState()
  }

  minimizeAll() {
    this.windows.forEach((data, windowId) => {
      if (!data.minimized) {
        const controller = this.application.getControllerForElementAndIdentifier(
          data.element,
          "window"
        )
        controller?.minimize()
      }
    })
    
    // Save state after minimizing all
    this.saveWindowsState()
  }

  closeAll() {
    this.windows.forEach((data, windowId) => {
      const controller = this.application.getControllerForElementAndIdentifier(
        data.element,
        "window"
      )
      controller?.close()
    })
  }

  getVisibleWindows() {
    const visible = []
    this.windows.forEach((data, windowId) => {
      if (!data.minimized) {
        visible.push(windowId)
      }
    })
    return visible
  }

  // ==========================================
  // Real-time Updates (Action Cable)
  // ==========================================

  setupCatalogChannel() {
    this.consumer = createConsumer()
    
    this.catalogSubscription = this.consumer.subscriptions.create(
      { channel: "CatalogChannel" },
      {
        received: (data) => this.handleCatalogUpdate(data)
      }
    )
  }

  handleCatalogUpdate(data) {
    // Dispatch custom event for all windows to react
    const event = new CustomEvent("catalog:updated", {
      detail: data,
      bubbles: true
    })
    this.containerTarget.dispatchEvent(event)
  }

  // ==========================================
  // Utilities
  // ==========================================

  generateId() {
    return Math.random().toString(36).substring(2, 9)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  showMaxWindowsWarning() {
    toast.error(`Máximo ${this.maxWindowsValue} ventanas permitidas. Cierra alguna ventana para abrir otra.`)
  }

  // ==========================================
  // State Persistence (sessionStorage)
  // ==========================================

  /**
   * Saves current windows state to sessionStorage.
   * Uses sessionStorage (instead of localStorage) so each browser tab
   * maintains its own independent window state.
   */
  saveWindowsState() {
    const windowsState = []
    
    this.windows.forEach((data, windowId) => {
      const element = data.element
      if (!element) return
      
      const controller = this.application.getControllerForElementAndIdentifier(element, "window")
      const isMaximized = element.classList.contains("is-maximized")
      
      // Get position and size (use stored values if maximized)
      let x, y, width, height
      if (isMaximized && controller?.preMaximizeState) {
        x = controller.preMaximizeState.x
        y = controller.preMaximizeState.y
        width = controller.preMaximizeState.width
        height = controller.preMaximizeState.height
      } else {
        const rect = element.getBoundingClientRect()
        const containerRect = this.containerTarget.getBoundingClientRect()
        x = rect.left - containerRect.left
        y = rect.top - containerRect.top
        width = rect.width
        height = rect.height
      }
      
      windowsState.push({
        title: data.title,
        src: data.src,
        originalSrc: data.originalSrc || data.src,
        x,
        y,
        width,
        height,
        minimized: data.minimized,
        maximized: isMaximized,
        zIndex: data.zIndex,
        navHistory: controller?.navHistory || [],
        navIndex: controller?.navIndex ?? -1
      })
    })
    
    // Sort by zIndex to maintain window order
    windowsState.sort((a, b) => a.zIndex - b.zIndex)
    
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({
        windows: windowsState,
        savedAt: Date.now()
      }))
    } catch (e) {
      console.warn("Failed to save windows state:", e)
    }
  }

  /**
   * Loads windows state from sessionStorage
   */
  loadWindowsState() {
    try {
      const data = sessionStorage.getItem(this.storageKey)
      if (!data) return null
      
      const state = JSON.parse(data)
      
      // Optional: Check if state is too old (e.g., 7 days)
      const maxAge = 7 * 24 * 60 * 60 * 1000 // 7 days in ms
      if (state.savedAt && (Date.now() - state.savedAt) > maxAge) {
        this.clearWindowsState()
        return null
      }
      
      return state
    } catch (e) {
      console.warn("Failed to load windows state:", e)
      return null
    }
  }

  /**
   * Clears saved windows state
   */
  clearWindowsState() {
    try {
      sessionStorage.removeItem(this.storageKey)
    } catch (e) {
      console.warn("Failed to clear windows state:", e)
    }
  }
}
